import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import ErrorBoundary from './components/shared/ErrorBoundary';
import Layout from './components/ui/Layout';
import { startNotificationMonitoring, scheduleDailyReminder } from './lib/notificationTriggers';
import { listenForSubscriptionChanges } from './lib/pushNotifications';

const LoginForm = lazy(() => import('./components/auth/LoginForm'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Welcome = lazy(() => import('./pages/Welcome'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Projects = lazy(() => import('./pages/Projects'));
const Tasks = lazy(() => import('./pages/Tasks'));
const Clients = lazy(() => import('./pages/Clients'));
const Budgets = lazy(() => import('./pages/Budgets'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Profitability = lazy(() => import('./pages/Profitability'));
const Forecast = lazy(() => import('./pages/Forecast'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Gantt = lazy(() => import('./pages/Gantt'));
const TimeTracking = lazy(() => import('./pages/TimeTracking'));
const Settings = lazy(() => import('./pages/Settings'));
const Pipeline = lazy(() => import('./pages/Pipeline'));
const Equipment = lazy(() => import('./pages/Equipment'));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-950">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
  </div>
);

function ProtectedRoute({
  children,
  requireOnboarded = true,
}: {
  children: React.ReactNode;
  requireOnboarded?: boolean;
}) {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireOnboarded) {
    // Invitado autenticado sin perfil todavía → completar alta.
    if (!profile) {
      return <Navigate to="/welcome" replace />;
    }
    // Perfil con onboarding pendiente (explícitamente false) → activación.
    if (profile.onboarded === false) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  return <>{children}</>;
}

function AppContent() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    const cleanup = startNotificationMonitoring();
    scheduleDailyReminder();
    listenForSubscriptionChanges();
    return cleanup;
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage onLogin={() => navigate('/login')} />} />
          <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginForm onBack={() => navigate('/')} />} />

          <Route path="/welcome" element={user ? <ErrorBoundary><Welcome /></ErrorBoundary> : <Navigate to="/login" replace />} />
          <Route path="/onboarding" element={<ProtectedRoute requireOnboarded={false}><ErrorBoundary><Onboarding /></ErrorBoundary></ProtectedRoute>} />

          <Route path="/dashboard" element={<ProtectedRoute><Layout><ErrorBoundary><Dashboard /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute><Layout><ErrorBoundary><Projects /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute><Layout><ErrorBoundary><Tasks /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/pipeline" element={<ProtectedRoute><Layout><ErrorBoundary><Pipeline /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><Layout><ErrorBoundary><Clients /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/budgets" element={<ProtectedRoute><Layout><ErrorBoundary><Budgets /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute><Layout><ErrorBoundary><Invoices /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/profitability" element={<ProtectedRoute><Layout><ErrorBoundary><Profitability /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/forecast" element={<ProtectedRoute><Layout><ErrorBoundary><Forecast /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><Layout><ErrorBoundary><Calendar /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/gantt" element={<ProtectedRoute><Layout><ErrorBoundary><Gantt /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/equipment" element={<ProtectedRoute><Layout><ErrorBoundary><Equipment /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/time-tracking" element={<ProtectedRoute><Layout><ErrorBoundary><TimeTracking /></ErrorBoundary></Layout></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Layout><ErrorBoundary><Settings /></ErrorBoundary></Layout></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;